import { ValidatorIdentifier } from './validator-identifier';

export enum ValidationRuleType {
    Required = 'required',
    Pattern = 'pattern',
    MaxLength = 'maxLength',
    MinLength = 'minLength',
    Min = 'min',
    Max = 'max',
    Custom = 'custom',
    AsyncCustom = 'asyncCustom',
    Precision = 'precision',
}

/**
 * Base validation rule interface
 */
export interface BaseValidationRule {
    type: ValidationRuleType;
    message?: string | string[];
}

/**
 * Required field validation rule
 */
export interface RequiredValidationRule extends BaseValidationRule {
    type: ValidationRuleType.Required;
}

/**
 * Pattern (regex) validation rule
 */
export interface PatternValidationRule extends BaseValidationRule {
    type: ValidationRuleType.Pattern;
    value: RegExp | string;
}

/**
 * Maximum length validation rule
 */
export interface MaxLengthValidationRule extends BaseValidationRule {
    type: ValidationRuleType.MaxLength;
    value: number;
}

/**
 * Minimum length validation rule
 */
export interface MinLengthValidationRule extends BaseValidationRule {
    type: ValidationRuleType.MinLength;
    value: number;
}

/**
 * Minimum value validation rule
 */
export interface MinValidationRule extends BaseValidationRule {
    type: ValidationRuleType.Min;
    value: number;
}

/**
 * Maximum value validation rule
 */
export interface MaxValidationRule extends BaseValidationRule {
    type: ValidationRuleType.Max;
    value: number;
}

/**
 * Custom validation rule
 */
export interface CustomValidationRule extends BaseValidationRule {
    type: ValidationRuleType.Custom;
    identifier: ValidatorIdentifier;
}

/**
 * Async custom validation rule
 */
export interface AsyncCustomValidationRule extends BaseValidationRule {
    type: ValidationRuleType.AsyncCustom;
    identifier: ValidatorIdentifier;
}

/**
 * Precision validation rule for decimal places
 */
export interface PrecisionValidationRule extends BaseValidationRule {
    type: ValidationRuleType.Precision;
    value: number;
}

/**
 * Union type for all validation rules
 */
export type ValidationRule =
    | RequiredValidationRule
    | PatternValidationRule
    | MaxLengthValidationRule
    | MinLengthValidationRule
    | MinValidationRule
    | MaxValidationRule
    | CustomValidationRule
    | AsyncCustomValidationRule
    | PrecisionValidationRule;

/**
 * Type guard for RequiredValidationRule
 */
export function isRequiredRule(rule: ValidationRule): rule is RequiredValidationRule {
    return rule.type === ValidationRuleType.Required;
}

/**
 * Type guard for PatternValidationRule
 */
export function isPatternRule(rule: ValidationRule): rule is PatternValidationRule {
    return rule.type === ValidationRuleType.Pattern;
}

/**
 * Type guard for MaxLengthValidationRule
 */
export function isMaxLengthRule(rule: ValidationRule): rule is MaxLengthValidationRule {
    return rule.type === ValidationRuleType.MaxLength;
}

/**
 * Type guard for MinLengthValidationRule
 */
export function isMinLengthRule(rule: ValidationRule): rule is MinLengthValidationRule {
    return rule.type === ValidationRuleType.MinLength;
}

/**
 * Type guard for MinValidationRule
 */
export function isMinRule(rule: ValidationRule): rule is MinValidationRule {
    return rule.type === ValidationRuleType.Min;
}

/**
 * Type guard for MaxValidationRule
 */
export function isMaxRule(rule: ValidationRule): rule is MaxValidationRule {
    return rule.type === ValidationRuleType.Max;
}

/**
 * Type guard for CustomValidationRule
 */
export function isCustomRule(rule: ValidationRule): rule is CustomValidationRule {
    return rule.type === ValidationRuleType.Custom;
}

/**
 * Type guard for AsyncCustomValidationRule
 */
export function isAsyncCustomRule(rule: ValidationRule): rule is AsyncCustomValidationRule {
    return rule.type === ValidationRuleType.AsyncCustom;
}

/**
 * Type guard for PrecisionValidationRule
 */
export function isPrecisionRule(rule: ValidationRule): rule is PrecisionValidationRule {
    return rule.type === ValidationRuleType.Precision;
}
